import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServerOrFail } from '@adornis/bh-util/util.js';
import { DateTime } from 'luxon';
import { Participation } from '../entities/Participation.js';
import { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission } from '../enums/roles.js';
import { createCalendarFeedToken } from '../server/calendar-feed-token.js';
import { mongo } from '../server/mongo.js';

@Entity()
export class InstitutionDashboardStats extends AdornisEntity {
  static override _class = 'InstitutionDashboardStats';

  @Field(type => Int)
  workshopsThisMonth!: number;

  @Field(type => Int)
  seatsTotal!: number;

  @Field(type => Int)
  seatsBooked!: number;

  @Field(type => Int)
  bookingsThisMonth!: number;
}

async function ensureDashboardPermission(institutionID: string) {
  const user = await getCurrentUserServerOrFail();
  if (!user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, institutionID)) throw new Error('no permission');
  return user;
}

export const getCalendarFeedToken = registerQuery({
  operationName: 'getCalendarFeedToken',
  params: [
    { name: 'feedType', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  type: () => String,
  resolve: (feedType: string, institutionID: string) => {
    return async () => {
      const user = await getCurrentUserServerOrFail();

      if (feedType === 'institution') {
        if (!institutionID) throw new Error('institutionID not given');
        if (!user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, institutionID))
          throw new Error('no permission');
        return createCalendarFeedToken(institutionID, user._id);
      }
      if (feedType === 'user' || feedType === 'lecturer') return createCalendarFeedToken(feedType, user._id);

      throw new Error('unsupported calendar feed type');
    };
  },
});

export const getUpcomingWorkshopsForInstitution = registerQuery({
  operationName: 'getUpcomingWorkshopsForInstitution',
  params: [
    { name: 'institutionID', type: () => String },
    { name: 'limit', type: () => Int },
  ],
  type: () => [Workshop],
  resolve: (institutionID: string, limit: number) => {
    return async <Selection extends BaseQLSelectionSet<Workshop>>(_gqlFields: Selection) => {
      await ensureDashboardPermission(institutionID);

      const cappedLimit = Math.min(Math.max(Math.trunc(limit || 0), 0), 10);
      if (cappedLimit === 0) return [];

      const now = new Date();
      const workshopCollection = mongo().collection(Workshop._collectionName);
      const orderedIDs = await workshopCollection.raw
        .aggregate<{ _id: string }>([
          {
            $match: {
              _class: Workshop._class,
              institutionID,
              isCanceled: { $ne: true },
              appointments: { $elemMatch: { start: { $gte: now } } },
            },
          },
          {
            $set: {
              nextAppointmentStart: {
                $min: {
                  $map: {
                    input: {
                      $filter: {
                        input: '$appointments',
                        as: 'appointment',
                        cond: { $gte: ['$$appointment.start', now] },
                      },
                    },
                    as: 'appointment',
                    in: '$$appointment.start',
                  },
                },
              },
            },
          },
          { $sort: { nextAppointmentStart: 1, _id: 1 } },
          { $limit: cappedLimit },
          { $project: { _id: 1 } },
        ])
        .toArray();

      const workshopIDs = orderedIDs.map(({ _id }) => _id);
      if (workshopIDs.length === 0) return [];

      const workshops = (await loadMongoData(Workshop, { _id: { $in: workshopIDs } })) as Workshop[];
      const workshopsByID = new Map(workshops.map(workshop => [workshop._id, workshop]));
      return workshopIDs
        .map(workshopID => workshopsByID.get(workshopID))
        .filter((workshop): workshop is Workshop => !!workshop);
    };
  },
});

type MonthWorkshopSummary = {
  workshopsThisMonth: number;
  seatsTotal: number;
  workshopIDs: string[];
};

type CountResult = { total: number };

export const getInstitutionDashboardStats = registerQuery({
  operationName: 'getInstitutionDashboardStats',
  params: [{ name: 'institutionID', type: () => String }],
  type: () => InstitutionDashboardStats,
  resolve: (institutionID: string) => {
    return async <Selection extends BaseQLSelectionSet<InstitutionDashboardStats>>(_gqlFields: Selection) => {
      await ensureDashboardPermission(institutionID);

      const berlinNow = DateTime.now().setZone('Europe/Berlin');
      const monthStart = berlinNow.startOf('month').toJSDate();
      const monthEnd = berlinNow.endOf('month').toJSDate();
      const activeStates = [ParticipationState.in, ParticipationState.participated];

      const workshopCollection = mongo().collection(Workshop._collectionName);
      const institutionProductIDsQuery = workshopCollection
        .find({ _class: Workshop._class, institutionID, isCanceled: { $ne: true } }, { projection: { _id: 1 } })
        .toArray();
      const workshopSummaryQuery = workshopCollection.raw
        .aggregate<MonthWorkshopSummary>([
          {
            $match: {
              _class: Workshop._class,
              institutionID,
              isCanceled: { $ne: true },
              appointments: { $elemMatch: { start: { $gte: monthStart, $lte: monthEnd } } },
            },
          },
          {
            $group: {
              _id: null,
              workshopsThisMonth: { $sum: 1 },
              seatsTotal: { $sum: { $ifNull: ['$capacity', 0] } },
              workshopIDs: { $push: '$_id' },
            },
          },
        ])
        .toArray();
      const [institutionProductDocs, [workshopSummary]] = await Promise.all([
        institutionProductIDsQuery,
        workshopSummaryQuery,
      ]);
      const institutionProductIDs = institutionProductDocs.map(({ _id }) => _id);

      const workshopIDs = workshopSummary?.workshopIDs ?? [];
      const participationCollection = mongo().collection(Participation._collectionName);

      const seatsBookedQuery =
        workshopIDs.length === 0
          ? Promise.resolve([] as CountResult[])
          : participationCollection.raw
              .aggregate<CountResult>([
                { $match: { productID: { $in: workshopIDs }, state: { $in: activeStates } } },
                { $count: 'total' },
              ])
              .toArray();

      const bookingsThisMonthQuery = participationCollection.raw
        .aggregate<CountResult>([
          {
            $match: {
              productID: { $in: institutionProductIDs },
              createdAt: { $gte: monthStart, $lte: monthEnd },
              state: { $in: activeStates },
            },
          },
          { $count: 'total' },
        ])
        .toArray();

      const [seatsBookedResult, bookingsThisMonthResult] = await Promise.all([
        seatsBookedQuery,
        bookingsThisMonthQuery,
      ]);

      return new InstitutionDashboardStats({
        workshopsThisMonth: workshopSummary?.workshopsThisMonth ?? 0,
        seatsTotal: workshopSummary?.seatsTotal ?? 0,
        seatsBooked: seatsBookedResult[0]?.total ?? 0,
        bookingsThisMonth: bookingsThisMonthResult[0]?.total ?? 0,
      });
    };
  },
});
